<script>
import { required, maxLength, requiredIf, email } from "@vuelidate/validators";
import {
  tel,
  zipCode,
  numCarteFidelite,
  codeBarre
} from "@/utils/form-validators";
import { useForms } from "@/components/organisms/forms/mixin";
import Button from "@/components/atoms/button";
import Form from "@/components/molecules/form";
import FormGroup from "@/components/molecules/form-group";
import FormInput from "@/components/molecules/form-input";
import {
  formValidationProps,
  useFormValidation
} from "@/components/organisms/mixins/form-validation.mixin";
import FormRadio from "@/components/molecules/form-radio";
import FormSelect from "@/components/molecules/form-select";
import FormSelectOption from "@/components/molecules/form-select/form-select-option";
import FormSelectOptGroup from "@/components/molecules/form-select/form-select-optgroup";
import ButtonIcon from "@/components/molecules/button-icon";
import Popin from "@/components/molecules/popin";
import Captcha from "@/components/molecules/captcha";
import MatchShops from "@/utils/match-shops";
import SuccessPopin from "@/components/organisms/forms/successPopin";
import Container from "@/components/atoms/container";
import ButtonGroup from "@/components/molecules/button-group";
import FormFieldset from "@/components/molecules/form-fieldset";

export default {
  name: "ProductInfosForm",
  components: {
    FormFieldset,
    ButtonGroup,
    Form,
    FormSelectOptGroup,
    FormSelect,
    FormGroup,
    FormSelectOption,
    FormInput,
    Button,
    FormRadio,
    ButtonIcon,
    Popin,
    Captcha,
    SuccessPopin,
    Container
  },
  props: {
    magasins: {
      type: Array,
      required: true
    },
    magasinId: {
      type: Number,
      required: false,
      default: 121
    },
    ...formValidationProps()
  },
  emits: ["save"],
  setup(props) {
    const { captcha, v$, getState, getErrorMessage, checkValidation, states } =
      useFormValidation(props);

    const { redirectToPreviousPage } = useForms();

    return {
      redirectToPreviousPage,
      captcha,
      v$,
      getState,
      getErrorMessage,
      checkValidation,
      states
    };
  },
  data() {
    return {
      form: {
        enseigne: "Carrefour",
        num_carte_match: "",
        num_commande: "",
        magasin_id: this.magasinId ? this.magasinId : "",
        civilite: "",
        nom: "",
        prenom: "",
        adresse: "",
        complement_adresse: "",
        cp: "",
        ville: "",
        email: "",
        tel: "",
        nom_produit: "",
        code_barre: "",
        dlc_dluo: "",
        num_lot: "",
        message: ""
      },
      popinVisible: false,
      popinTitle: "",
      popinText: "",
      popinImage: "",
      captchaToken: null,
      matchShops: MatchShops
    };
  },
  computed: {
    otherMagasins() {
      if (!this.magasinId) {
        return this.magasins;
      }
      return this.magasins.filter((magasin) => {
        return parseInt(magasin.id) !== this.magasinId;
      });
    },
    currentMagasin() {
      return this.magasins.find((magasin) => {
        return parseInt(magasin.id) === this.magasinId;
      });
    }
  },
  validations() {
    return {
      form: {
        enseigne: {
          required
        },
        magasin_id: {
          required: requiredIf(function () {
            return (
              this.form.enseigne === "Cora" || this.form.enseigne === "Match"
            );
          })
        },
        nom: {
          required
        },
        prenom: {
          required
        },
        civilite: {
          required
        },
        adresse: {
          required
        },
        cp: {
          required,
          zipCode
        },
        ville: {
          required
        },
        email: {
          required,
          email,
          maxLength: maxLength(60)
        },
        tel: {
          tel
        },
        num_carte_match: {
          numCarteFidelite
        },
        nom_produit: {
          required
        },
        code_barre: {
          codeBarre
        },
        num_commande: {},
        message: {
          maxLength: maxLength(4000)
        },
        dlc_dluo: {
          maxLength: maxLength(10)
        }
      }
    };
  },
  watch: {
    currentMagasin: {
      handler(value) {
        if (value) {
          this.form.magasin_id = this.currentMagasin.id;
          this.v$.form.magasin_id.$touch();
        }
      },
      immediate: true
    }
  },
  methods: {
    save() {
      if (this.checkValidation(this.v$)) {
        this.$refs.captcha.execute();
      }
    },
    showPopin(title, text, imageUrl = "") {
      this.popinVisible = true;
      this.popinTitle = title;
      this.popinText = text;
      this.popinImage = imageUrl;
    },
    closePopin() {
      this.popinVisible = false;
    },
    verifyCaptcha(token) {
      if (token) {
        this.captchaToken = token;
      }
      const model = {
        ...this.form,
        "g-captcha-response": this.captchaToken
      };
      this.$emit("save", model);
    }
  }
};
</script>
<template>
  <div class="u-mb-xlg u-pl-md u-pr-md u-bgcolor-white u-pb-md">
    <Container>
      <br>
      <p class="c-inscription-form__explanation">
        Vous avez une question sur un produit ? Merci de compléter le formulaire suivant :
      </p>
      <p class="c-inscription-form__explanation">
        *Les champs marqués d'un astérisque sont obligatoires.
      </p>
      <br>
    </Container>
    <SuccessPopin
      v-if="state===states.SUCCESS"
      @hidden="redirectToPreviousPage()"
    />
    <Form
      :error="error"
      class="u-mx-xxlg u-mx-lg@md"
      @submit.prevent="save()"
    >
      <FormFieldset size="full">
        <label class="u-block u-font-13 u-mb-xs u-color-text-light">Mon enseigne *</label><br>
        <FormGroup
          :state="getState(v$,'enseigne', 'form')"
          class="c-inscription-form__form-group"
          size="full"
        >
          <FormFieldset size="full">
            <FormRadio
              v-model="form.enseigne"
              name="enseigne"
              value="Carrefour"
            >
              <span>Carrefour</span>
            </FormRadio>

            <FormRadio
              v-model="form.enseigne"
              class="u-mt-sm"
              name="enseigne"
              value="Cora"
            >
              Cora
            </FormRadio>

            <FormRadio
              v-model="form.enseigne"
              class="u-mt-sm"
              name="enseigne"
              value="Match"
            >
              <span>Supermarché Match</span>
            </FormRadio>

            <FormRadio
              v-model="form.enseigne"
              class="u-mt-sm"
              name="enseigne"
              value="Migros"
            >
              Migros
            </FormRadio>

            <FormRadio
              v-model="form.enseigne"
              class="u-mt-sm"
              name="enseigne"
              value="Maximo"
            >
              Maximo
            </FormRadio>
            <FormRadio
              v-model="form.enseigne"
              name="enseigne"
              value="Houra"
              class="u-mt-sm"
            >
              Houra
            </FormRadio>
          </FormFieldset>
          <template #message>
            <p>
              <template v-if="v$.form.enseigne.required.$invalid">
                Merci de renseigner votre enseigne
              </template>
              <template v-else>
                {{ getErrorMessage("enseigne") }}
              </template>
            </p>
          </template>
        </FormGroup>
        <div
          v-if="form.enseigne === 'Carrefour'"
          class="u-full-width u-flex u-flex-align-center u-mt-md u-flex-column"
        >
          <p class="c-inscription-form__explanation u-text-center">Afin d'obtenir des informations au sujet des produits Carrefour, merci de nous contacter via le bouton ci-dessous.</p>
          <Button
            class="u-fit-content"
            variant="secondary"
            radius="full"
            size="md"
            target="_blank"
            to="https://www.carrefour.fr/services/nous-contacter"
          >
            Contacter Carrefour
          </Button>
        </div>
        <template v-else>
          <FormGroup
            v-if="form.enseigne === 'Match'"
            :state="getState(v$,'magasin_id', 'form')"
            class="c-inscription-form__form-group"
            size="full"
          >
            <label for="magasinDemande">
              <h2 class="u-font-19 u-mb-xs">
                Magasin Supermarché Match concerné par votre demande *
              </h2>
            </label>
            <FormSelect
              id="magasinDemande"
              v-model="form.magasin_id"
              :state="getState(v$,'magasin_id', 'form')"
              :default-value="'Sélectionnez un magasin'"
              size="full"
            >
              <FormSelectOptGroup label=" magasins Supermarché Match">
                <FormSelectOption
                  v-for="magasin in matchShops"
                  :key="magasin.id"
                  :value="magasin.id"
                >
                  {{ magasin.label }}
                </FormSelectOption>
              </FormSelectOptGroup>
            </FormSelect>
            <template #message>
              <p>
                <template v-if="v$.form.magasin_id.required.$invalid">
                  Merci de choisir un magasin
                </template>
                <template v-else>
                  {{ getErrorMessage("magasin_id") }}
                </template>
              </p>
            </template>
          </FormGroup>
          <FormGroup
            v-if="form.enseigne === 'Cora'"
            :state="getState(v$,'magasin_id', 'form')"
            class="c-inscription-form__form-group"
            size="full"
          >
            <label
              for="magasinReference"
              class="u-block u-font-13 u-mb-xs u-color-text-light"
            > Votre magasin de référence*
            </label>
            <FormSelect
              id="magasinReference"
              v-model="form.magasin_id"
              :state="getState(v$,'magasin_id', 'form')"
              :default-value="currentMagasin ? null : 'Sélectionnez un magasin'"
              size="full"
            >
              <FormSelectOptGroup
                v-if="currentMagasin"
                label="Vous êtes rattaché(e) au magasin"
              >
                <FormSelectOption :value="currentMagasin.id">
                  {{ currentMagasin.attributes.magasin }}
                </FormSelectOption>
              </FormSelectOptGroup>
              <FormSelectOptGroup label="Autres magasins Cora">
                <FormSelectOption
                  v-for="magasin in otherMagasins"
                  :key="magasin.id"
                  :value="magasin.id"
                >
                  {{ magasin.attributes.magasin }}
                </FormSelectOption>
              </FormSelectOptGroup>
            </FormSelect>
            <template #message>
              <p>
                <template v-if="v$.form.magasin_id.required.$invalid">
                  Merci de choisir un magasin
                </template>
                <template v-else>
                  {{ getErrorMessage("magasin_id") }}
                </template>
              </p>
            </template>
          </FormGroup>
          <label class="u-block u-font-13 u-mb-xs u-color-text-light">Civilité *</label>
          <FormGroup
            :state="getState(v$,'civilite', 'form')"
            inline
            class="c-inscription-form__form-group"
            size="full"
          >
            <FormRadio
              v-model="form.civilite"
              name="civilite"
              value="Mme"
              class="u-mr-lg"
            >
              Madame
            </FormRadio>
            <FormRadio
              v-model="form.civilite"
              name="civilite"
              value="M."
            >
              Monsieur
            </FormRadio>
            <template #message>
              <p>
                <template v-if="v$.form.civilite.required.$invalid">
                  Merci de renseigner votre civilité
                </template>
                <template v-else>
                  {{ getErrorMessage("civilite") }}
                </template>
              </p>
            </template>
          </FormGroup>

          <FormGroup
            :state="getState(v$,'nom', 'form')"
            class="c-inscription-form__form-group"
            size="full"
          >
            <FormInput
              id="nom"
              v-model.trim="form.nom"
              :state="getState(v$,'nom', 'form')"
              maxlength="60"
              size="full"
              @blur="v$.form.nom.$touch()"
              @input="v$.form.nom.$reset()"
            >
              Nom *
            </FormInput>
            <template #message>
              <p>
                <template v-if="v$.form.nom.required.$invalid">
                  Merci de renseigner votre nom
                </template>
                <template v-else>
                  {{ getErrorMessage("nom") }}
                </template>
              </p>
            </template>
          </FormGroup>

          <FormGroup
            :state="getState(v$,'prenom', 'form')"
            class="c-inscription-form__form-group"
            size="full"
          >
            <FormInput
              id="prenom"
              v-model.trim="form.prenom"
              :state="getState(v$,'prenom', 'form')"
              maxlength="40"
              size="full"
              @blur="v$.form.prenom.$touch()"
              @input="v$.form.prenom.$reset()"
            >
              Prénom *
            </FormInput>
            <template #message>
              <p>
                <template v-if="v$.form.prenom.required.$invalid">
                  Merci de renseigner votre prénom
                </template>
                <template v-else>
                  {{ getErrorMessage("prenom") }}
                </template>
              </p>
            </template>
          </FormGroup>
          <FormGroup
            :state="getState(v$,'adresse', 'form')"
            class="c-inscription-form__form-group"
            size="full"
          >
            <FormInput
              id="adresse"
              v-model.trim="form.adresse"
              :state="getState(v$,'adresse', 'form')"
              maxlength="255"
              size="full"
              @blur="v$.form.adresse.$touch()"
              @input="v$.form.adresse.$reset()"
            >
              Adresse *
            </FormInput>
            <template #message>
              <p>
                <template v-if="v$.form.nom.required.$invalid">
                  "Merci de renseigner votre Adresse
                </template>
                <template v-else>
                  {{ getErrorMessage("nom") }}
                </template>
              </p>
            </template>
          </FormGroup>
          <FormGroup
            :state="getState(v$,'complement_adresse', 'form')"
            class="c-inscription-form__form-group"
            size="full"
          >
            <FormInput
              id="complement"
              v-model.trim="form.complement_adresse"
              :state="getState(v$,'complement_adresse', 'form')"
              size="full"
            >
              Complément
            </FormInput>
            <template #message>
              <p>
                {{ getErrorMessage("complement_adresse") }}
              </p>
            </template>
          </FormGroup>
          <FormGroup
            :state="getState(v$,'cp', 'form')"
            class="c-inscription-form__form-group"
            size="full"
          >
            <FormInput
              id="cp"
              v-model.trim="form.cp"
              :state="getState(v$,'cp', 'form')"
              size="full"
              @blur="v$.form.cp.$touch()"
              @input="v$.form.cp.$reset()"
            >
              Code Postal *
            </FormInput>
            <template #message>
              <p>
                <template v-if="v$.form.cp.required.$invalid">
                  Merci de renseigner votre code postal
                </template>
                <template v-else>
                  {{ getErrorMessage("cp") }}
                </template>
              </p>
            </template>
          </FormGroup>

          <FormGroup
            :state="getState(v$,'ville', 'form')"
            class="c-inscription-form__form-group"
            size="full"
          >
            <FormInput
              id="ville"
              v-model.trim="form.ville"
              :state="getState(v$,'ville', 'form')"
              size="full"
              @blur="v$.form.ville.$touch()"
              @input="v$.form.ville.$reset()"
            >
              Ville *
            </FormInput>
            <template #message>
              <p>
                <template v-if="v$.form.ville.required.$invalid">
                  Merci de renseigner votre ville
                </template>
                <template v-else>
                  {{ getErrorMessage("ville") }}
                </template>
              </p>
            </template>
          </Formgroup>

          <FormGroup
            :state="getState(v$,'email','form')"
            class="c-inscription-form__form-group"
            size="full"
          >
            <FormInput
              id="email"
              v-model.trim="form.email"
              :state="getState(v$,'email','form')"
              size="full"
              @blur="v$.form.email.$touch()"
              @input="v$.form.email.$reset()"
            >
              Email *
            </FormInput>
            <template #message>
              <p>
                <template v-if="v$.form.email.required.$invalid">
                  Merci de confirmer votre adresse e-mail
                </template>
                <template v-else-if="v$.form.email.email.$invalid">
                  Le format de cette adresse e-mail est invalide
                </template>
                <template v-else>
                  {{ getErrorMessage("email") }}
                </template>
              </p>
            </template>
          </FormGroup>
          <FormGroup
            :state="getState(v$,'tel', 'form')"
            class="c-inscription-form__form-group"
            size="full"
          >
            <FormInput
              id="tel"
              v-model.trim="form.tel"
              :state="getState(v$,'tel', 'form')"
              size="full"
              @blur="v$.form.tel.$touch()"
              @input="v$.form.tel.$reset()"
            >
              Téléphone
            </FormInput>
            <template #message>
              <p>
                <template v-if="v$.form.tel.required.$invalid">
                  Merci de renseigner votre numéro de téléphone
                </template>
                <template v-if="v$.form.tel.tel.$invalid">
                  Merci de saisir une valeur valide
                </template>
                <template v-else>
                  {{ getErrorMessage("tel") }}
                </template>
              </p>
            </template>
          </FormGroup>
          <FormGroup
            v-show="form.enseigne === 'Match'"
            :state="getState(v$,'num_carte_match', 'form')"
            class="c-inscription-form__form-group"
            size="full"
          >
            <FormInput
              id="num_carte_match"
              v-model="form.num_carte_match"
              :state="getState(v$,'num_carte_match', 'form')"
              size="full"
              @blur="v$.form.num_carte_match.$touch()"
              @input="v$.form.num_carte_match.$reset()"
            >
              N° de carte match
            </FormInput>
            <template #message>
              <p>
                <template v-if="v$.form.num_carte_match.numCarteFidelite.$invalid">
                  Ce numéro de carte est invalide
                </template>
                <template v-else>
                  {{ getErrorMessage("num_carte_match") }}
                </template>
              </p>
            </template>
          </FormGroup>

          <FormGroup
            :state="getState(v$,'nom_produit', 'form')"
            class="c-inscription-form__form-group"
            size="full"
          >
            <FormInput
              id="nomProduit"
              v-model.trim="form.nom_produit"
              :state="getState(v$,'nom_produit', 'form')"
              size="full"
            >
              Nom du produit *
            </FormInput>
            <template #message>
              <p>
                <template v-if="v$.form.nom_produit.required.$invalid">
                  Merci de renseigner le nom du produit
                </template>
                <template v-else>
                  {{ getErrorMessage("nom_produit") }}
                </template>
              </p>
            </template>
          </FormGroup>
          <label class="u-block u-font-13 u-mb-xs u-color-text-light">
            Code barre :
            <ButtonIcon
              icon-name="info-circle"
              icon-color="text-light"
              radius="full"
              size="xs"
              @click="showPopin('Code barre', 'Saisissez le numéro indiqué sous le code barre', 'https://www.cora.fr/fileadmin/design/img/fcontact/ean-small.png')"
            />
          </label>
          <FormGroup
            :state="getState(v$,'code_barre', 'form')"
            class="c-inscription-form__form-group"
            size="full"
          >
            <FormInput
              id="codeBarre"
              v-model.trim="form.code_barre"
              :state="getState(v$,'code_barre', 'form')"
              size="full"
            >
              Code barre
            </FormInput>
            <template #message>
              <p>
                <template v-if="v$.form.code_barre.codeBarre.$invalid">
                  Merci de saisir un code à barre valide
                </template>
                <template v-else>
                  {{ getErrorMessage("code_barre") }}
                </template>
              </p>
            </template>
          </FormGroup>
          <label class="u-block u-font-13 u-mb-xs u-color-text-light">
            DLC / DLUO :
            <ButtonIcon
              icon-name="info-circle"
              icon-color="text-light"
              radius="full"
              size="xs"
              @click="showPopin('DLC / DLUO', 'Saisissez la date limite de consommation (DLC) ou Date limite d’utilisation optimale (DLUO)', 'https://www.cora.fr/fileadmin/design/img/fcontact/dlc-small.png')"
            />
          </label>
          <FormGroup
            :state="getState(v$,'dlc_dluo', 'form')"
            class="c-inscription-form__form-group"
            size="full"
          >
            <FormInput
              id="DlcDluo"
              v-model.trim="form.dlc_dluo"
              :state="getState(v$,'dlc_dluo', 'form')"
              type="date"
              size="full"
              @blur="v$.form.dlc_dluo.$touch()"
              @input="v$.form.dlc_dluo.$reset()"
            />
            <template #message>
              <p>
                <template v-if="v$.form.dlc_dluo.maxLength.$invalid">
                  merci de saisir une date valide
                </template>
                {{ getErrorMessage("dlc_dluo") }}
              </p>
            </template>
          </FormGroup>
          <label class="u-block u-font-13 u-mb-xs u-color-text-light">
            N° de lot / série :
            <ButtonIcon
              icon-name="info-circle"
              icon-color="text-light"
              radius="full"
              size="xs"
              @click="showPopin('N° de lot / série', 'Saisissez le numéro de lot ou de série du produit', 'https://www.cora.fr/fileadmin/design/img/fcontact/numlot-small.png')"
            />
          </label>
          <FormGroup
            :state="getState(v$,'num_lot', 'form')"
            class="c-inscription-form__form-group"
            size="full"
          >
            <FormInput
              id="numLot"
              v-model.trim="form.num_lot"
              :state="getState(v$,'num_lot', 'form')"
              size="full"
            >
              Numéro de lot / numéro de série
            </FormInput>
            <template #message>
              <p>
                {{ getErrorMessage("num_lot") }}
              </p>
            </template>
          </FormGroup>
          <FormGroup
            :state="getState(v$,'num_commande', 'form')"
            class="c-inscription-form__form-group"
            size="full"
          >
            <label class="u-block u-font-13 u-mb-xs u-color-text-light"> Numéro de commande
            </label>
            <FormInput
              id="numCommande"
              v-model.trim="form.num_commande"
              :state="getState(v$,'num_commande','form')"
              size="full"
              @blur="v$.form.num_commande.$touch()"
              @input="v$.form.num_commande.$reset()"
            >
              exemple : DRV-MB-564564
            </FormInput>
            <template #message>
              <p>
                {{ getErrorMessage("num_commande") }}
              </p>
            </template>
          </FormGroup>
          <FormGroup
            :state="getState(v$,'message', 'form')"
            class="c-inscription-form__form-group"
            size="full"
          >
            <FormInput
              id="message"
              v-model.trim="form.message"
              :state="getState(v$,'message', 'form')"
              size="full"
              type="textarea"
              @blur="v$.form.message.$touch()"
              @input="v$.form.message.$reset()"
            >
              Votre message
            </FormInput>
            <template #message>
              <p>
                <template v-if="v$.form.message.required.$invalid">
                  Merci de renseigner votre message
                </template>
                <template v-else>
                  {{ getErrorMessage("message") }}
                </template>
              </p>
            </template>
          </FormGroup>
          <p class="c-inscription-form__required-field">
            Champs obligatoires*
          </p>
          <Captcha
            ref="captcha"
            @verify="verifyCaptcha"
          />
          <ButtonGroup center>
            <Button
              variant="secondary"
              radius="full"
              type="submit"
              value="Je valide ma demande"
              size="md"
              :disabled="state === states.LOADING"
              class="u-mt-sm u-flex-justify-center@sm"
            />
          </ButtonGroup>
        </template>

      </FormFieldset>
    </Form>
    <Popin
      :visible="popinVisible"
      :alertdialog="true"
      :aria-label="popinTitle"
      @cancelled="closePopin"
    >
      <h2>{{ popinTitle }}</h2>
      <br>
      <p>{{ popinText }}</p>
      <br>
      <img
        :src="popinImage"
        :alt="popinTitle"
      >
    </Popin>
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/styles/components/inscription-form.scss";
</style>
